import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { Glossary } from '@/services/apis/glossaries';
import { GuideLanguage } from '@/services/apis/guides';

interface Props {
  htmlContent: string;
  glossaries: Glossary[];
  currentLanguage?: GuideLanguage;
}

export function RichTextWithGlossaries({ htmlContent, glossaries, currentLanguage = GuideLanguage.EN }: Props) {
  /**
   * Build Map of LOWERCASE → definition
   * Supports both term and aliases.
   */
  const glossaryMap = useMemo(() => {
    const map = new Map<string, Glossary>();

    glossaries.forEach((g) => {
      map.set(g.name.toLowerCase(), g);

      if (g.aliases?.length) {
        g.aliases.forEach((alias) => {
          map.set(alias.toLowerCase(), g);
        });
      }
    });

    return map;
  }, [glossaries, currentLanguage]);

  /**
   * Highlight glossary terms (unique, only first occurrence)
   * Produces sanitized HTML.
   */
  const processedHtml = useMemo(() => {
    let html = htmlContent || '';
    const seen = new Set<string>();

    // Extract all words (English + Spanish chars)
    const regex = /\b[A-Za-záéíóúüñÁÉÍÓÚÜÑ]{3,}\b/g;

    html = html.replace(regex, (match) => {
      const lower = match.toLowerCase();
      if (seen.has(lower)) return match;
      if (!glossaryMap.has(lower)) return match;

      // Found unique glossary term
      seen.add(lower);

      const glossary = glossaryMap.get(lower);
      const definition = glossary?.translations?.find((t) => t.language === currentLanguage)?.definition || '';

      // Inject tooltip-enabled Tailwind markup to display at the top of the term on hover.
      return `
        <span 
          class="relative overflow-visible group cursor-help text-blue-600 underline decoration-dotted"
        >
          ${match}
          <span class="absolute glossary-tooltip left-1/2 w-[300px] bottom-full mt-2 
              hidden group-hover:block
              px-4 py-2
              text-xs text-white bg-gray-900
              rounded shadow-lg z-50">
            <span class="flex items-start space-x-2">
                <svg class="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                </svg>
                <span class="flex flex-col">
                    <span class="font-semibold text-blue-400 mb-1">${glossary?.name}</span>
                    ${
                      glossary?.category?.name
                        ? `<span class="text-xs text-gray-400 mb-2 capitalize">${glossary?.category?.name}</span>`
                        : ''
                    }
                    <span class="text-sm text-gray-200 leading-relaxed">${definition}</span>
                </span>
            </span>
          </span>
        </span>
      `;
    });

    return DOMPurify.sanitize(html);
  }, [htmlContent, glossaryMap]);

  return <div dangerouslySetInnerHTML={{ __html: processedHtml }} />;
}
